/**
 * The active-collection switcher: one component for the sidebar (full width,
 * with the star toggle) and the top bar when the sidebar is collapsed (a
 * compact chip, the name truncated). Same state (CollectionContext), same menu,
 * same keyboard behaviour (MUI Select: arrows, Enter, Esc) plus a search field
 * at the top of the menu that filters collections by name.
 */
import React, { useState } from 'react';
import { Box, FormControl, InputBase, ListSubheader, MenuItem, Select, Tooltip } from '@mui/material';
import { Star } from '@mui/icons-material';
import { mvkgTokens, readerTokens as R, shellTokens as SH } from '../../theme/mvkgTokens';
import { useCollections } from '../../contexts/CollectionContext';
import StarToggle from '../home/StarToggle';
import { useStarredCollection } from '../home/starredCollection';

const notAnOption = (el) => {
  if (el) { el.setAttribute('role', 'presentation'); el.removeAttribute('aria-selected'); }
};

export default function CollectionSwitcher({ compact = false }) {
  const { collections, selectedCollection, setSelectedCollection } = useCollections();
  const { id: starredId } = useStarredCollection();
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuMinW, setMenuMinW] = useState(undefined);
  if (!collections?.length) return null;
  const value = selectedCollection || 'all';
  const current = value === 'all' ? null : collections.find((c) => String(c.id) === String(value));
  const label = current?.name || 'All collections';
  const q = query.trim().toLowerCase();
  // The active collection stays listed so the Select always has its value.
  const shown = collections.filter((c) => !q || c.name.toLowerCase().includes(q) || String(c.id) === String(value));

  const select = (
    <FormControl fullWidth={!compact} size="small" sx={compact ? { width: 'auto', maxWidth: 220, flexShrink: 0 } : undefined}>
      <Select
        value={value}
        inputProps={{ 'aria-label': 'Active collection' }}
        SelectDisplayProps={{ 'aria-label': `Active collection: ${label}` }}
        onChange={(e) => setSelectedCollection(e.target.value)}
        onOpen={() => setMenuOpen(true)}
        onClose={() => { setMenuOpen(false); setQuery(''); setMenuMinW(undefined); }}
        renderValue={(v) => (v === 'all' ? 'All collections' : collections.find((c) => String(c.id) === String(v))?.name || v)}
        MenuProps={{
          autoFocus: false,
          // The menu opens unfiltered; keep that width so filtering never shrinks it.
          TransitionProps: { onEntering: (node) => setMenuMinW(node.offsetWidth) },
          // style, not sx: Select writes the anchor's width as an inline min-width.
          PaperProps: { sx: { maxHeight: 420 }, style: menuMinW ? { minWidth: menuMinW } : undefined },
        }}
        sx={{
          height: compact ? 36 : 34, borderRadius: `${SH.controlRadius}px`, bgcolor: SH.surface, fontSize: R.size.ui, fontWeight: 600,
          '& .MuiSelect-select': compact ? { pr: '28px !important', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : undefined,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: R.ruleStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(15,23,42,0.28)' },
          '& .MuiSelect-select:focus-visible': { outline: `2px solid ${mvkgTokens.brand.main}`, outlineOffset: 2, borderRadius: `${SH.controlRadius}px` },
        }}
      >
        {/* Select stamps role="option" on every child; the search row is not one. */}
        <ListSubheader ref={notAnOption} sx={{ bgcolor: SH.surface, px: 1, py: 0.75, lineHeight: 'normal' }}>
          <InputBase
            autoFocus
            fullWidth
            placeholder="Find a collection"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // Keys stay in the field (MUI's type-ahead would steal letters); arrows still reach the list.
            onKeyDown={(e) => { if (e.key !== 'Escape' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') e.stopPropagation(); }}
            inputProps={{ 'aria-label': 'Find a collection' }}
            sx={{ fontSize: R.size.ui, px: 1, py: 0.25, border: `1px solid ${R.ruleStrong}`, borderRadius: `${SH.controlRadius}px` }}
          />
        </ListSubheader>
        {(!q || value === 'all') && <MenuItem value="all">All collections</MenuItem>}
        {q && shown.length === 0 && <MenuItem disabled>No collection matches</MenuItem>}
        {shown.map((c) => (
          <MenuItem key={c.id} value={String(c.id)}>
            {c.name}
            {Number(c.id) === starredId && (
              <Star aria-label="starred" sx={{ ml: 'auto', pl: 1, fontSize: 16, color: mvkgTokens.brand.main }} />
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {/* The chip truncates a long name: the full one is on hover (not on focus,
          and never over the open menu). */}
      {compact ? <Tooltip title={menuOpen ? '' : label} placement="bottom-start" enterDelay={500} disableFocusListener><Box sx={{ minWidth: 0 }}>{select}</Box></Tooltip> : select}
      {/* Star the active collection: Home recommends from the starred one. */}
      {current && <StarToggle collection={current} size={compact ? 34 : 32} />}
    </Box>
  );
}
